import React, { useMemo, useState } from 'react';
import type { Client } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useClientDrawerStore } from '@hero/hooks-shared';
import { useClientApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { PencilIcon, LocationAddIcon, ContactIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { ClientSummaryTab } from './ClientSummaryTab';
import { ClientLocationsTab } from './ClientLocationsTab';
import { ClientOrdersTab } from './ClientOrdersTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { ClientEditModal } from '../Modal/ClientEditModal';
import { LocationEditModal } from '../../SavedLocation/Modal/SavedLocationEditModal';
import { ContactEditModal } from '../../Contact/Modal/ContactEditModal';

export interface ClientDrawerProps {
  displayForUser: 'customer' | 'carrier';
  syncUrl?: boolean | string;
}

interface ClientDrawerContentProps {
  client: Client;
  displayForUser: 'customer' | 'carrier';
  onClose: () => void;
}

const ClientDrawerContent: React.FC<ClientDrawerContentProps> = ({
  client,
  displayForUser,
  onClose,
}) => {
  const { t } = useTranslation(['m-client']);
  const { isEnabled: canEditClient } = useFeature('customer_module', 'sub_edit_customer');
  const { isEnabled: canCreateSavedLocation } = useFeature(
    'saved_location_module',
    'sub_create_saved_location',
  );
  const { isEnabled: canCreateContact } = useFeature('contact_module', 'sub_create_contact');
  const [activeTab, setActiveTab] = useState('summary');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const tabs = [
    { id: 'summary', label: t('drawer.tabs.summary') },
    { id: 'locations', label: t('drawer.tabs.locations') },
    { id: 'orders', label: t('drawer.tabs.orders') },
  ];

  const clientName = client.name || '';

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'new-location',
        label: t('drawer.new_location'),
        description: t('drawer.new_location_description', { name: clientName }),
        icon: <LocationAddIcon className="w-4 h-4" />,
        onClick: () => setLocationModalOpen(true),
        hide: !canCreateSavedLocation,
      },
      {
        id: 'new-contact',
        label: t('drawer.new_contact'),
        description: t('drawer.new_contact_description', { name: clientName }),
        icon: <ContactIcon className="w-4 h-4" />,
        onClick: () => setContactModalOpen(true),
        hide: !canCreateContact,
      },
      {
        id: 'edit',
        label: t('drawer.edit'),
        description: t('drawer.edit_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setEditModalOpen(true),
        hide: !canEditClient,
      },
    ],
    [t, clientName, canEditClient, canCreateSavedLocation, canCreateContact],
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <ClientSummaryTab client={client} />;
      case 'locations':
        return <ClientLocationsTab client={client} />;
      case 'orders':
        return <ClientOrdersTab client={client} displayForUser={displayForUser} />;
      default:
        return null;
    }
  };

  // Subtitle = first role title (or "+N" if more than one).
  const primaryRole = client.roles?.[0];
  const subtitle = primaryRole
    ? client.roles.length > 1
      ? `${t(`roles.${primaryRole}.title`)} +${client.roles.length - 1}`
      : t(`roles.${primaryRole}.title`)
    : '';

  return (
    <>
      <DrawerHeader title={clientName} subtitle={subtitle} onClose={onClose} />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
      <DrawerFooter actions={actions} buttonLabel={t('drawer.actions')} />

      <ClientEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        clientId={client._id}
      />

      <LocationEditModal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onLocationSaved={() => setLocationModalOpen(false)}
        mode="create"
        clientId={client._id}
        forceType="client"
      />

      <ContactEditModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        customerId={client._id}
      />
    </>
  );
};

/**
 * Stacked drawer for the unified `Client` CRM entity (relations-v2).
 * Replaces `CustomerDrawer`. URL param contract: `clientId`.
 */
export const ClientDrawer: React.FC<ClientDrawerProps> = ({ displayForUser, syncUrl }) => (
  <StackedDrawer<Client>
    useStore={useClientDrawerStore}
    useItemApi={id => useClientApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="clientId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Client Details"
    description="View client information"
    renderContent={(client, onClose) => (
      <ClientDrawerContent client={client} displayForUser={displayForUser} onClose={onClose} />
    )}
  />
);
